import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { listConversations, listBuyerPresales, listSellerPresales, buyerCounterRespond, parseInquiryError, REASON_CODE_LABELS } from "@/lib/modulD";
import { ArrowLeft, Send, MessageSquare, ShoppingBag, Sparkles, Plus, Lock, Clock, Check, X, AlertCircle, Bell, Trash2, Loader2 } from "lucide-react";
import FanLevelBadge from "@/components/fan/FanLevelBadge";
import {  TabBar, PillChip, InfoCard  } from "@/components/toggles";

const TABS = [
  { key: "social", label: "Social", icon: MessageSquare },
  { key: "marketplace", label: "Marketplace", icon: ShoppingBag },
  { key: "stx", label: "stilltouchedX", icon: Sparkles },
  { key: "system", label: "System", icon: Bell },
];

export default function MessagesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // Tab in URL persistieren, damit Back-Navigation den richtigen Tab wiederherstellt
  const urlTab = new URLSearchParams(location.search).get("tab");
  const VALID_TABS = ["social", "marketplace", "stx", "system"];
  const initialTab = VALID_TABS.includes(urlTab) ? urlTab : "social";
  const [tab, setTab] = useState(initialTab);
  const [unread, setUnread] = useState({});

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/messages/unread"); setUnread(data); } catch { /* ignore */ }
  }, []);

  // Sync URL → tab state when user navigates back (e.g. browser back button)
  useEffect(() => {
    const tabFromUrl = new URLSearchParams(location.search).get("tab");
    if (VALID_TABS.includes(tabFromUrl) && tabFromUrl !== tab) {
      setTab(tabFromUrl);
    }
  }, [location.search, tab]);

  // Tab change → write to URL (replace to avoid extra history entries)
  const changeTab = (newTab) => {
    setTab(newTab);
    navigate(`/messages?tab=${newTab}`, { replace: true });
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    loadUnread();
    const onRefresh = () => { loadUnread(); };
    window.addEventListener("unread-refresh", onRefresh);
    return () => window.removeEventListener("unread-refresh", onRefresh);
  }, [user, authLoading, navigate, loadUnread]);

  return (
    <div data-testid="messages-page" className="w-full max-w-3xl mx-auto">
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-lg font-bold text-[#0A0A0A]">{t("messages_extra.title")}</h1>
      </div>
      <TabBar
        tabs={TABS.map((tabDef) => ({
          key: tabDef.key,
          label: t(`messages_extra.tab_${tabDef.key}`, tabDef.label),
          badge: unread[tabDef.key === "stx" ? "stilltouchedx" : tabDef.key] || 0,
          testId: `msg-tab-${tabDef.key}`,
        }))}
        activeKey={tab}
        onChange={changeTab}
      />
      {tab === "social" && <SocialTab />}
      {tab === "marketplace" && <MarketplaceTab />}
      {tab === "stx" && <STXTab />}
      {tab === "system" && <SystemTab />}
    </div>
  );
}

// ─── System Messages Tab (Sub-Batch 4c.5.4) ─────────────
function SystemTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/messages/system");
        setMessages(data.items || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDelete = async (message_id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("messages_extra.confirm_remove_msg"))) return;
    setDeletingId(message_id);
    try {
      await api.delete(`/messages/system/${message_id}`);
      setMessages((prev) => prev.filter((m) => m.message_id !== message_id));
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  const KIND_LABEL = {
    ADMIN_NOTICE: { label: "stilltouched-Team", color: "bg-blue-50 border-blue-200 text-blue-800" },
    ACCOUNT_NOTICE: { label: "Konto-Hinweis", color: "bg-red-50 border-red-200 text-red-800" },
    REPORT_OUTCOME: { label: "Meldungs-Ergebnis", color: "bg-amber-50 border-amber-200 text-amber-800" },
    PLATFORM_ANNOUNCEMENT: { label: "Ankündigung", color: "bg-purple-50 border-purple-200 text-purple-800" },
    UPDATE: { label: "Update", color: "bg-green-50 border-green-200 text-green-800" },
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="system-messages">
      {messages.length === 0 ? (
        <EmptyState text="Keine Systemnachrichten." sub="Hier erscheinen wichtige Mitteilungen vom stilltouched-Team." />
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {messages.map((m) => {
            const meta = KIND_LABEL[m.kind] || { label: "Nachricht", color: "bg-gray-50 border-gray-200 text-gray-800" };
            return (
              <div
                key={m.message_id}
                className="group relative w-full px-4 py-3 text-left hover:bg-[#FAFAFA] transition-colors"
              >
                <button
                  data-testid={`system-msg-${m.message_id}`}
                  onClick={() => navigate(`/messages/system/${m.message_id}`)}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#0A0A0A] flex items-center justify-center shrink-0">
                      <Bell size={16} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0 pr-8">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`px-1.5 py-0.5 text-[9px] font-medium border ${meta.color}`}>{meta.label}</span>
                        {!m.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-label="Ungelesen" />}
                      </div>
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{m.subject || meta.label}</p>
                      <p className="text-xs text-[#666] truncate mt-0.5">{m.body}</p>
                      <p className="text-[10px] text-[#999] mt-1">{new Date(m.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</p>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid={`system-msg-delete-${m.message_id}`}
                  onClick={(e) => handleDelete(m.message_id, e)}
                  disabled={deletingId === m.message_id}
                  className="absolute top-3 right-3 p-1.5 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                  aria-label={t("messages_extra.delete_msg")}
                  title={t("messages_extra.delete_msg")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SocialTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [requests, setRequests] = useState([]);
  const [actingId, setActingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  // iter88.16: Fan-Level pro Thread-Partner (Seller-View)
  const [fanLevels, setFanLevels] = useState({});
  const reload = useCallback(async () => {
    try {
      const [thRes, reqRes] = await Promise.all([
        api.get("/messages/social/threads"),
        api.get("/messages/social/requests").catch(() => ({ data: { requests: [] } })),
      ]);
      const list = thRes.data.threads || [];
      setThreads(list);
      setRequests(reqRes.data.requests || []);
      const buyerIds = list.map((th) => th.other_user?.person_id || th.other_user?.user_id).filter(Boolean);
      if (buyerIds.length > 0) {
        try {
          const { data: lvl } = await api.post("/fan-level/bulk-lookup", { buyer_ids: buyerIds });
          setFanLevels(lvl.levels || {});
        } catch { /* Buyer-View → no permission, silently skip */ }
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { (async () => { await reload(); setLoading(false); })(); }, [reload]);

  const handleRequest = async (thread_id, action) => {
    setActingId(thread_id + action);
    try {
      await api.post(`/messages/social/requests/${thread_id}/${action}`);
      if (action === "accept") {
        navigate(`/messages/social/${thread_id}`);
      } else {
        setRequests((prev) => prev.filter((r) => r.thread_id !== thread_id));
      }
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
      await reload();
    } catch { /* ignore */ }
    finally { setActingId(null); }
  };

  const handleDelete = async (thread_id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("messages_extra.confirm_remove_conv"))) return;
    setDeletingId(thread_id);
    try {
      await api.delete(`/messages/social/threads/${thread_id}`);
      setThreads((prev) => prev.filter((t) => t.thread_id !== thread_id));
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
    } catch { /* ignore */ }
    finally { setDeletingId(null); }
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="social-messages">
      {/* Nachrichten-Anfragen (Erstkontakt muss freigegeben werden) */}
      {requests.length > 0 && (
        <div data-testid="social-requests" className="border-b border-[#E5E5E5] bg-[#FFFDF7]">
          <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#999]">
            Nachrichten-Anfragen ({requests.length})
          </p>
          <div className="divide-y divide-[#F0F0F0]">
            {requests.map((r) => (
              <div key={r.thread_id} data-testid={`msg-request-${r.thread_id}`} className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999] shrink-0">
                  {r.requester?.avatar_url ? <img src={r.requester.avatar_url} alt="" className="w-full h-full object-cover" /> : (r.requester?.display_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{r.requester?.display_name || "User"}</p>
                  <p className="text-xs text-[#666] truncate">möchte dir schreiben{r.preview ? `: „${r.preview.slice(0, 60)}"` : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`msg-request-accept-${r.thread_id}`}
                    onClick={() => handleRequest(r.thread_id, "accept")}
                    disabled={!!actingId}
                    className="h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#222] disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    {actingId === r.thread_id + "accept" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Annehmen
                  </button>
                  <button
                    data-testid={`msg-request-reject-${r.thread_id}`}
                    onClick={() => handleRequest(r.thread_id, "reject")}
                    disabled={!!actingId}
                    className="h-8 px-3 rounded-full border border-[#E5E5E5] text-[#666] text-xs font-semibold hover:bg-[#F5F5F5] disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    <X size={12} /> Ablehnen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {threads.length === 0 && requests.length === 0 ? (
        <EmptyState text="Noch keine Social-Nachrichten." sub="Schreibe einem Creator über sein Profil." />
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {threads.map((th) => (
            <div key={th.thread_id} className="group relative hover:bg-[#FAFAFA] transition-colors">
              <button data-testid={`dm-thread-${th.thread_id}`}
                onClick={() => navigate(`/messages/social/${th.thread_id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left pr-11">
                <div className="w-10 h-10 rounded-full bg-[#E5E5E5] flex items-center justify-center text-sm font-bold text-[#999] shrink-0">
                  {(th.other_user?.display_name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className={`text-sm truncate ${th.unread > 0 ? "font-bold text-[#0A0A0A]" : "font-medium text-[#0A0A0A]"}`}>
                        {th.other_user?.display_name || "User"}
                      </p>
                      {th.pending_request && (
                        <span data-testid={`dm-pending-${th.thread_id}`} className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold shrink-0">Warte auf Freigabe</span>
                      )}
                      <FanLevelBadge
                        level={fanLevels[th.other_user?.person_id || th.other_user?.user_id]}
                        area="MP"
                        showLabel={false}
                      />
                    </div>
                    <span className="text-[10px] text-[#CCC] shrink-0">{th.last_message_at ? timeAgo(th.last_message_at) : ""}</span>
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${th.unread > 0 ? "text-[#0A0A0A] font-medium" : "text-[#999]"}`}>{th.last_message || "..."}</p>
                  {/* iter80.1: Post-Kontext-Hinweis */}
                  {th.post_context && (
                    <p className="text-[10px] text-[#666] truncate mt-0.5 flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 bg-[#666]"></span>
                      Bezogen auf Beitrag: {(th.post_context.text || "").slice(0, 40)}
                      {th.post_context.text && th.post_context.text.length > 40 ? "…" : ""}
                    </p>
                  )}
                </div>
                {th.unread > 0 && <span className="w-5 h-5 bg-[#0A0A0A] text-white text-[9px] font-bold rounded-full flex items-center justify-center shrink-0">{th.unread}</span>}
              </button>
              <button
                type="button"
                data-testid={`dm-thread-delete-${th.thread_id}`}
                onClick={(e) => handleDelete(th.thread_id, e)}
                disabled={deletingId === th.thread_id}
                className="absolute top-1/2 -translate-y-1/2 right-3 p-1.5 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                aria-label={t("messages_extra.delete_conv")}
                title={t("messages_extra.delete_conv")}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MarketplaceTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    try {
      const [inq, after] = await Promise.all([
        listConversations({ shop_area: "MARKETPLACE", kind: "MARKETPLACE_PRE_SALE_INQUIRY" }).catch(() => ({ conversations: [] })),
        listConversations({ shop_area: "MARKETPLACE", kind: "AFTER_SALE_CHAT" }).catch(() => ({ conversations: [] })),
        ]);
      const merged = [
        ...(inq.conversations || []).map(c => ({ ...c, _type: "INQUIRY" })),
        ...(after.conversations || []).map(c => ({ ...c, _type: "AFTER_SALE" })),
      ].sort((a, b) => new Date(b.updated_at || b.last_message_at || 0) - new Date(a.updated_at || a.last_message_at || 0));
      setItems(merged);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleteConv = async (item) => {
    if (!window.confirm(t("messages_extra.confirm_remove_conv"))) return;
    try {
      await api.delete(`/modul-d/conversations/${item.conversation_id}`);
      setItems((prev) => prev.filter((x) => x.conversation_id !== item.conversation_id));
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
    } catch { /* ignore */ }
  };

  if (loading) return <Spinner />;
  const filtered = filter === "ALL" ? items : items.filter(i => i._type === filter);
  const inqCount = items.filter(i => i._type === "INQUIRY").length;
  const aftCount = items.filter(i => i._type === "AFTER_SALE").length;

  return (
    <div data-testid="mp-messages">
      <SubTypeFilter
        filter={filter}
        setFilter={setFilter}
        counts={{ ALL: items.length, INQUIRY: inqCount, AFTER_SALE: aftCount }}
        labels={{ ALL: "Alle", INQUIRY: "MP-Anfragen", AFTER_SALE: "After-Sale" }}
      />
      {filtered.length === 0 ? (
        <EmptyState text="Keine Marketplace-Nachrichten." sub="Anfragen und After-Sale Chats erscheinen hier." />
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {filtered.map((row) => <ConversationListRow key={row.conversation_id} item={row} onClick={() => navigate(`/messages/inquiry/${row.conversation_id}`)} onDelete={handleDeleteConv} />)}
        </div>
      )}
    </div>
  );
}

function ConversationListRow({ item, onClick, onDelete }) {
  const { t } = useTranslation();
  const typeBadge = item._type === "INQUIRY"
    ? <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 font-bold uppercase tracking-wider">{t("messages_extra.mp_request")}</span>
    : item._type === "AFTER_SALE"
    ? <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold uppercase tracking-wider">After-Sale Chat</span>
    : item._type === "PRESALE_INCOMING"
    ? <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 font-bold uppercase tracking-wider">Pre-Sale Eingang</span>
    : item._type === "PRESALE_OUTGOING"
    ? <span className="text-[9px] px-1.5 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 font-bold uppercase tracking-wider">Pre-Sale</span>
    : null;
  const cid = item.conversation_id || item.presale_id;
  return (
    <div className="group relative hover:bg-[#FAFAFA] transition-colors">
      <button data-testid={`conv-${cid}`}
        onClick={onClick}
        className="w-full flex items-center gap-3 px-4 py-3 text-left pr-11">
        <div className="w-10 h-12 bg-[#F5F5F5] shrink-0 overflow-hidden">
          {item.listing?.media_urls?.[0] && <img src={item.listing.media_urls[0]} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {typeBadge}
            <p className="text-xs text-[#999] truncate">{item.other_display_name}</p>
          </div>
          <p className="text-sm font-medium text-[#0A0A0A] truncate mt-0.5">{item.listing?.title || item.title || "Konversation"}</p>
          <p className="text-xs text-[#CCC] truncate mt-0.5">{item.last_message || item.preview || "Noch keine Nachricht"}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {item.unread > 0 && <span className="w-5 h-5 bg-[#0A0A0A] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{item.unread}</span>}
          {item.status === "CLOSED" ? (
            <span className="text-[10px] px-2 py-0.5 border text-[#999] border-[#E5E5E5] bg-[#FAFAFA]">Geschlossen</span>
          ) : item.sub_status === "WAITING_FOR_REPLY" ? (
            <span className="text-[10px] px-2 py-0.5 border text-amber-600 border-amber-200 bg-amber-50">{t("messages_extra.reply_open")}</span>
          ) : item.status === "OPEN" ? (
            <span className="text-[10px] px-2 py-0.5 border text-green-600 border-green-200 bg-green-50">Offen</span>
          ) : null}
        </div>
      </button>
      {onDelete && (
        <button
          type="button"
          data-testid={`conv-delete-${cid}`}
          onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          className="absolute top-3 right-3 p-1.5 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          aria-label={t("messages_extra.remove_from_overview")}
          title={t("messages_extra.remove_from_overview")}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function SubTypeFilter({ filter, setFilter, counts, labels }) {
  return (
    <div className="border-b border-[#E5E5E5] bg-white overflow-x-auto">
      <div className="flex gap-2 px-4 py-3 min-w-max">
        {Object.keys(labels).map((k) => (
          <PillChip
            key={k}
            testId={`filter-${k.toLowerCase()}`}
            active={filter === k}
            onClick={() => setFilter(k)}
            variant="neutral"
            size="sm"
          >
            {labels[k]}{counts[k] !== undefined ? ` (${counts[k]})` : ""}
          </PillChip>
        ))}
      </div>
    </div>
  );
}

function STXTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [presaleIncoming, setPresaleIncoming] = useState([]);
  const [presaleOutgoing, setPresaleOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [filter, setFilter] = useState("ALL");
  // iter88.16: Fan-Level pro Buyer für Seller-Presale-Rows
  const [fanLevels, setFanLevels] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [presBuyer, presSeller, conv, dmRes] = await Promise.all([
        listBuyerPresales().catch(() => ({ requests: [] })),
        listSellerPresales().catch(() => ({ requests: [] })),
        listConversations({ shop_area: "STILLTOUCHEDX", kind: "AFTER_SALE_CHAT" }).catch(() => ({ conversations: [] })),
        api.get("/messages/social/threads?area=STILLTOUCHEDX").then((r) => r.data).catch(() => ({ threads: [] })),
      ]);
      const outgoingIds = new Set((presBuyer.requests || []).map(r => r.presale_id));
      const outgoing = (presBuyer.requests || []).map(r => ({ ...r, _type: "PRESALE_OUTGOING" }));
      // Dedup: if a presale appears as both outgoing (buyer=me) AND incoming (seller=me),
      // prefer OUTGOING view (buyer perspective). Avoids duplicate React keys.
      const incoming = (presSeller.requests || [])
        .filter(r => !outgoingIds.has(r.presale_id))
        .map(r => ({ ...r, _type: "PRESALE_INCOMING" }));
      const afters = (conv.conversations || []).map(c => ({ ...c, _type: "AFTER_SALE" }));
      const dmThreads = (dmRes.threads || []).map(th => ({ ...th, _type: "STX_DM" }));
      setPresaleOutgoing(outgoing);
      setPresaleIncoming(incoming);
      // Fan-Level bulk-lookup für alle eingehenden Presales (Seller-View)
      const buyerIds = incoming.map((r) => r.buyer_id).filter(Boolean);
      if (buyerIds.length > 0) {
        try {
          const { data: lvl } = await api.post("/fan-level/bulk-lookup", { buyer_ids: buyerIds });
          setFanLevels(lvl.levels || {});
        } catch { /* noop */ }
      }
      // Merged chronological list
      const merged = [
        ...outgoing.map(r => ({ ...r, _ts: r.updated_at || r.created_at })),
        ...incoming.map(r => ({ ...r, _ts: r.updated_at || r.created_at })),
        ...afters.map(c => ({ ...c, _ts: c.updated_at || c.last_message_at || c.created_at })),
        ...dmThreads.map(d => ({ ...d, _ts: d.updated_at || d.last_message_at || d.created_at })),
      ].sort((a, b) => new Date(b._ts || 0) - new Date(a._ts || 0));
      setItems(merged);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCounter = async (presaleId, decision) => {
    setResponding(presaleId + decision);
    setErrorBanner(null);
    try {
      await buyerCounterRespond(presaleId, decision);
      await load();
    } catch (e) {
      const parsed = parseInquiryError(e);
      setErrorBanner({ msg: parsed.message || e?.response?.data?.detail || "Fehler" });
    } finally { setResponding(null); }
  };

  const handleDeletePresale = async (presaleId) => {
    if (!window.confirm(t("messages_extra.confirm_remove_presale"))) return;
    try {
      await api.delete(`/messages/stx/presale/${presaleId}`);
      setItems((prev) => prev.filter((i) => i.presale_id !== presaleId));
      setPresaleIncoming((prev) => prev.filter((i) => i.presale_id !== presaleId));
      setPresaleOutgoing((prev) => prev.filter((i) => i.presale_id !== presaleId));
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
    } catch { /* ignore */ }
  };

  const handleDeleteAfterSale = async (item) => {
    if (!window.confirm(t("messages_extra.confirm_remove_after_sale"))) return;
    try {
      await api.delete(`/modul-d/conversations/${item.conversation_id}`);
      setItems((prev) => prev.filter((i) => i.conversation_id !== item.conversation_id));
      try { window.dispatchEvent(new CustomEvent("unread-refresh")); } catch { /* ignore */ }
    } catch { /* ignore */ }
  };

  if (loading) return <Spinner />;
  const filtered = filter === "ALL" ? items : items.filter(i => i._type === filter || (filter === "PRESALE" && (i._type === "PRESALE_INCOMING" || i._type === "PRESALE_OUTGOING")));
  const incomingCount = presaleIncoming.length;
  const outgoingCount = presaleOutgoing.length;
  const afterCount = items.filter(i => i._type === "AFTER_SALE").length;

  return (
    <div data-testid="stx-messages">
      {errorBanner && (
        <InfoCard variant="danger" className="mx-4 mt-3">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{errorBanner.msg}</span>
        </InfoCard>
      )}
      <SubTypeFilter
        filter={filter}
        setFilter={setFilter}
        counts={{ ALL: items.length, PRESALE_INCOMING: incomingCount, PRESALE_OUTGOING: outgoingCount, AFTER_SALE: afterCount }}
        labels={{ ALL: "Alle", PRESALE_INCOMING: "Pre-Sale Eingang", PRESALE_OUTGOING: "Pre-Sale Gesendet", AFTER_SALE: "After-Sale" }}
      />
      {filtered.length === 0 ? (
        <EmptyState text="Keine STX-Nachrichten." sub="Pre-Sale Anfragen, After-Sale Chats und Direktnachrichten erscheinen hier." />
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {filtered.map((it) => {
            if (it._type === "PRESALE_OUTGOING") {
              return <BuyerPresaleCard key={it.presale_id} request={it}
                onCounter={(d) => handleCounter(it.presale_id, d)}
                working={responding === it.presale_id + "ACCEPT" || responding === it.presale_id + "DECLINE"}
                navigate={navigate}
                onDelete={() => handleDeletePresale(it.presale_id)} />;
            }
            if (it._type === "PRESALE_INCOMING") {
              return <SellerPresaleRow key={it.presale_id} request={it} navigate={navigate}
                fanLevel={fanLevels[it.buyer_id]}
                onDelete={() => handleDeletePresale(it.presale_id)} />;
            }
            if (it._type === "AFTER_SALE") {
              return <ConversationListRow key={it.conversation_id} item={it}
                onClick={() => navigate(`/messages/inquiry/${it.conversation_id}`)}
                onDelete={handleDeleteAfterSale} />;
            }
            if (it._type === "STX_DM") {
              const ou = it.other_user || {};
              return (
                <button
                  key={it.thread_id}
                  data-testid={`stx-dm-thread-${it.thread_id}`}
                  onClick={() => navigate(`/messages/social/${it.thread_id}`)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] text-left"
                >
                  <span className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F5F5] shrink-0 flex items-center justify-center">
                    {ou.avatar_url ? <img src={ou.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold text-[#999]">{(ou.display_name || "?")[0]}</span>}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-[#0A0A0A] truncate">{ou.display_name || "stilltouchedX-Creator"}</span>
                    {it.last_message && <span className="block text-xs text-[#999] truncate">{it.last_message}</span>}
                  </span>
                  {it.unread > 0 && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                </button>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}

function SellerPresaleRow({ request: r, navigate, onDelete, fanLevel }) {
  const { t } = useTranslation();
  const STATUS_LABEL = {
    SUBMITTED: "Neu", SELLER_ACCEPTED: "Akzeptiert",
    SELLER_MODIFIED: "Gegenangebot offen", SELLER_REJECTED: "Abgelehnt",
    TEMP_LISTING_CREATED: "Privates Angebot",
    BUYER_COUNTER_ACCEPTED: "Buyer hat angenommen", BUYER_COUNTER_DECLINED: "Buyer hat abgelehnt",
    CLOSED: "Beendet", CLOSED_ITEM_UNAVAILABLE: "Artikel nicht mehr verfügbar",
  };
  const STATUS_COLOR = {
    SUBMITTED: "text-amber-600 bg-amber-50 border-amber-200",
    SELLER_MODIFIED: "text-blue-700 bg-blue-50 border-blue-200",
    SELLER_REJECTED: "text-red-600 bg-red-50 border-red-200",
    TEMP_LISTING_CREATED: "text-green-700 bg-green-100 border-green-300",
    CLOSED: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
  };
  const isNew = r.status === "SUBMITTED";
  return (
    <div className="relative hover:bg-[#FAFAFA] transition-colors">
      <button data-testid={`incoming-presale-${r.presale_id}`}
        onClick={() => navigate(`/messages/presale/${r.presale_id}`)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left pr-11">
        <div className="w-10 h-12 bg-[#F5F5F5] shrink-0 overflow-hidden">
          {r.listing?.media_urls?.[0] && <img src={r.listing.media_urls[0]} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 font-bold uppercase tracking-wider">Pre-Sale Eingang</span>
            <p className="text-xs text-[#999] truncate">von {r.buyer_alias || "Buyer"}</p>
            <FanLevelBadge level={fanLevel} area="MP" showLabel={false} />
          </div>
          <p className="text-sm font-medium text-[#0A0A0A] truncate mt-0.5">{r.item_description?.slice(0, 80)}</p>
          {r.listing?.title && <p className="text-xs text-[#CCC] truncate mt-0.5">Bezug: {r.listing.title}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {isNew && <span className="w-5 h-5 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">!</span>}
          <span className={`text-[10px] px-2 py-0.5 border ${STATUS_COLOR[r.status] || "text-[#999] border-[#E5E5E5]"}`}>{STATUS_LABEL[r.status] || r.status}</span>
        </div>
      </button>
      {onDelete && (
        <button
          type="button"
          data-testid={`incoming-presale-delete-${r.presale_id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-3 right-3 p-1.5 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          aria-label={t("messages_extra.remove_request")}
          title={t("messages_extra.remove_request")}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function BuyerPresaleCard({ request: r, onCounter, working, navigate, onDelete }) {
  const { t } = useTranslation();
  const STATUS_LABEL = {
    SUBMITTED: "Ausstehend", SELLER_ACCEPTED: "Akzeptiert",
    SELLER_MODIFIED: "Gegenangebot erhalten", SELLER_REJECTED: "Abgelehnt",
    TEMP_LISTING_CREATED: "Privates Angebot verfügbar",
    BUYER_COUNTER_ACCEPTED: "Angenommen", BUYER_COUNTER_DECLINED: "Abgelehnt",
    CLOSED: "Beendet", CLOSED_ITEM_UNAVAILABLE: "Artikel nicht mehr verfügbar",
  };
  const STATUS_COLOR = {
    SUBMITTED: "text-amber-600 bg-amber-50 border-amber-200",
    SELLER_ACCEPTED: "text-green-600 bg-green-50 border-green-200",
    SELLER_MODIFIED: "text-blue-700 bg-blue-50 border-blue-200",
    SELLER_REJECTED: "text-red-600 bg-red-50 border-red-200",
    TEMP_LISTING_CREATED: "text-green-700 bg-green-100 border-green-300",
    CLOSED: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
    CLOSED_ITEM_UNAVAILABLE: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
  };
  const isCounterPending = r.status === "SELLER_MODIFIED" && r.buyer_counter_response_status === "PENDING";
  const tempListing = r.temp_listing;

  return (
    <div data-testid={`presale-${r.presale_id}`} className="relative hover:bg-[#FAFAFA] transition-colors">
      {onDelete && (
        <button
          type="button"
          data-testid={`presale-delete-${r.presale_id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-3 right-3 p-1.5 text-[#999] hover:text-red-600 hover:bg-red-50 rounded transition-colors z-10"
          aria-label={t("messages_extra.remove_request")}
          title={t("messages_extra.remove_request")}
        >
          <Trash2 size={14} />
        </button>
      )}
      <button onClick={() => navigate(`/messages/presale/${r.presale_id}`)} type="button"
        className="w-full text-left px-4 pt-3 pb-2 pr-11">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[9px] px-1.5 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 font-bold uppercase tracking-wider">Pre-Sale</span>
              <p className="text-[10px] text-[#999] truncate">an {r.seller?.display_name || r.seller?.handle || "Seller"}</p>
            </div>
            <p className="text-sm text-[#0A0A0A] line-clamp-2">{r.item_description?.slice(0, 120)}</p>
            {r.listing?.title && (
              <p className="text-[10px] text-[#CCC] mt-0.5">Bezug: {r.listing.title}</p>
            )}
          </div>
          <span className={`text-[10px] px-2 py-0.5 border shrink-0 ${STATUS_COLOR[r.status] || "text-[#999] border-[#E5E5E5]"}`}>
            {STATUS_LABEL[r.status] || r.status}
          </span>
        </div>
      </button>

      {/* Counter Offer Card (BUYER must decide) - inside card but buttons stopPropagation */}
      {isCounterPending && r.counter_offer && (
        <div className="mx-4 mb-3 px-3 py-3 bg-blue-50 border border-blue-200" onClick={(e) => e.stopPropagation()} data-testid={`counter-offer-${r.presale_id}`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">{t("messages_extra.counter_offer_seller")}</p>
          <p className="text-xl font-bold text-blue-900">{Number(r.counter_offer.price).toFixed(2)} EUR</p>
          {r.counter_offer.message && <p className="text-xs text-blue-800 mt-1">&ldquo;{r.counter_offer.message}&rdquo;</p>}
          <p className="text-[10px] text-blue-600 mt-2">Du kannst nur einmal antworten.</p>
          <div className="flex gap-2 mt-3">
            <button data-testid={`counter-accept-${r.presale_id}`} onClick={(e) => { e.stopPropagation(); onCounter("ACCEPT"); }} disabled={working}
              className="flex-1 h-9 bg-green-600 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40">
              <Check size={12} /> Annehmen
            </button>
            <button data-testid={`counter-decline-${r.presale_id}`} onClick={(e) => { e.stopPropagation(); onCounter("DECLINE"); }} disabled={working}
              className="flex-1 h-9 border border-red-300 text-red-600 text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40">
              <X size={12} /> Ablehnen
            </button>
          </div>
        </div>
      )}

      {/* Temp Listing CTA */}
      {r.status === "TEMP_LISTING_CREATED" && tempListing?.listing_id && (
        <div className="mx-4 mb-3 px-3 py-2 bg-green-50 border border-green-200" onClick={(e) => e.stopPropagation()}>
          <p className="text-xs font-bold text-green-700">{t("messages_extra.private_offer")}</p>
          <p className="text-xs text-green-700 mt-0.5">{Number(tempListing.price || 0).toFixed(2)} EUR · Nur für dich sichtbar</p>
          <button onClick={(e) => { e.stopPropagation(); navigate(`/checkout?listing=${tempListing.listing_id}&area=STILLTOUCHEDX&private=1`); }}
            className="mt-2 w-full h-9 bg-[#0A0A0A] text-white text-xs font-bold flex items-center justify-center gap-1">
            <ShoppingBag size={12} /> Jetzt kaufen
          </button>
        </div>
      )}

      {r.status === "SELLER_REJECTED" && (
        <p className="text-xs text-[#999] mt-2 italic">{t("messages_extra.request_rejected")}</p>
      )}

      <p className="text-[10px] text-[#CCC] mt-1.5">{r.created_at ? new Date(r.created_at).toLocaleString("de-DE") : ""}</p>
    </div>
  );
}

function EmptyState({ text, sub }) {
  return <div className="px-4 py-16 text-center"><p className="text-sm text-[#999]">{text}</p>{sub && <p className="text-xs text-[#CCC] mt-1">{sub}</p>}</div>;
}

function Spinner() {
  return <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "jetzt";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
